import { InjectionToken, NgZone } from '@angular/core';
import { Gtag, EventParams, CustomParams, Product, Promotion, Action, Content } from './gtag-definitions';
import { environment } from 'src/environments/environment';
import { UtilsService } from '../shared/services/utils.service';

export const GTAG = new InjectionToken<Gtag>('snack.gtag.instance');

export class GtagBaseService {

  constructor(
    private gtag: Gtag,
    private zone: NgZone,
    protected utils: UtilsService
  ) { }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/setting-values */
  protected set(params: CustomParams): void {
    if (!this.utils.isBrowser) {
      return;
    }
    return this.gtag('set', params);
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/events */
  protected event(action: string, params?: EventParams): Promise<void> {
    if (!this.utils.isBrowser) {
      return;
    }
    // Wraps the event call into a Promise
    if (!environment.production) {
      console.log('GTAG', action, params);
    }
    const promise = new Promise<void>((resolve, reject) => {
      try {
        // Triggers a 5s time-out timer
        const tmr = setTimeout(() => reject(), 5000);
        // Performs the event call resolving with the event callback
        this.gtag('event', action, { ...params, event_callback: () => { clearTimeout(tmr); resolve(); } });
      }
      // Rejects the promise on errors
      catch (e) { reject(e); }
    });
    promise.catch(() => {
      console.log('GTAG event timed out, most likely due to an ad blocker.');
    });
    return this.zone.runOutsideAngular(() => promise);
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/pages */
  protected sendPageView(title?: string, path?: string, location?: string) {
    return this.event('page_view', {
      page_title: title,
      page_path: path,
      page_location: location,
    });
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/exceptions */
  protected sendException(description?: string, fatal?: boolean) {
    return this.event('exception', { description, fatal });
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/user-timings */
  protected sendTimingComplete(name: string, value: number, event_category?: string, event_label?: string) {
    return this.event('timing_complete', { name, value, event_category, event_label });
  }

  /** @see: https://developers.google.com/analytics/devguides/collection/gtagjs/screens */
  protected sendScreenView(app_name: string, screen_name: string, app_id?: string, app_version?: string, app_installer_id?: string) {
    return this.event('screen_view', { app_name, screen_name, app_id, app_version, app_installer_id });
  }

  protected sendLogin(method?: string) {
    return this.event('login', { method });
  }

  protected sendSignUp(method?: string) {
    return this.event('sign_up', { method });
  }

  protected sendSearch(search_term?: string) {
    return this.event('search', { search_term });
  }

  protected sendSelectContent(content?: Content) {
    return this.event('select_content', content);
  }

  protected sendShare(method?: string, content?: Content) {
    return this.event('share', { method, ...content });
  }

  protected sendGenerateLead(action?: Action) {
    return this.event('generate_lead', action);
  }

  protected sendViewItem(items?: Product[]) {
    return this.event('view_item', { items, page_location: location.href });
  }

  protected sendViewItemList(items?: Product[]) {
    return this.event('view_item_list', { items, page_location: location.href });
  }

  protected sendViewPromotion(promotions?: Promotion[]) {
    return this.event('view_promotion', { promotions });
  }

  protected sendViewSearchResults(search_term?: string) {
    return this.event('view_search_results', { search_term });
  }

  protected sendAddPaymentInfo() {
    return this.event('add_payment_info');
  }

  protected sendAddToCart(action?: Action) {
    action.page_location = location.href;
    return this.event('add_to_cart', action);
  }

  protected sendClick(action?: Action) {
    return this.event('click', action);
  }
  protected sendOpen(action?: Action) {
    return this.event('open', action);
  }
  protected sendClose(action?: Action) {
    return this.event('close', action);
  }
  protected sendApply(action?: Action) {
    return this.event('apply', action);
  }
  protected sendRemove(action?: Action) {
    return this.event('remove', action);
  }

  protected sendTimePlayed(action?: Action) {
    return this.event('time_played', action);
  }

  protected sendViewClick(action?: Action) {
    return this.event('view_click', action);
  }

  protected sendPlay(action?: Action) {
    return this.event('play', action);
  }

  protected sendStop(action?: Action) {
    return this.event('stop', action);
  }

  protected sendMissingField(action?: Action) {
    return this.event('missing_field', action);
  }

  protected sendPassword(action?: Action) {
    return this.event('password', action);
  }

  protected sendAddToWishlist(action?: Action) {
    return this.event('add_to_wishlist', action);
  }

  protected sendBeginCheckout(action?: Action) {
    return this.event('begin_checkout', action);
  }

  protected sendCheckoutProgress(action?: Action) {
    return this.event('checkout_progress', action);
  }

  protected sendPurchase(action?: Action) {
    return this.event('purchase', action);
  }

  protected sendConversion(action?: Action) {
    return this.event('conversion', action);
  }

  protected sendRefund(action?: Action) {
    return this.event('refund', action);
  }

  protected sendRemoveFromCart(action?: Action) {
    return this.event('remove_from_cart', action);
  }

  protected sendSetCheckoutOption(checkout_step?: number, checkout_option?: string) {
    return this.event('set_checkout_option', { checkout_step, checkout_option });
  }

  protected sendSwitchToSubscription(action?: Action) {
    return this.event('begin_checkout', action);
  }



}
